import { Resolutions } from '@innovyze/stylovyze';

export type ResolutionOption = { label: string; value: Resolutions };
type UserType = 'standard' | 'advanced';

/**
 * Finds the closest valid resolution from a list, excluding any in the blacklist.
 * Otherwise, returns the first resolution not in the blacklist.
 *
 * @param currentResolution - The current resolution to compare.
 * @param listOfResolutions - Array of available resolutions.
 * @param blackList - Array of resolutions to exclude.
 * @returns The closest valid resolution.
 */
const findRes = (
  currentResolution: Resolutions,
  listOfResolutions: Resolutions[],
  blackList: Resolutions[]
) => {
  // * If current resolution is not blacklisted, return it
  if (!blackList.includes(currentResolution)) return currentResolution;
  // * Find the closest resolution not in the blacklist
  const closest = listOfResolutions.find((res) => !blackList.includes(res));
  return closest;
};

/**
 * Determines the closest resolution based on user type and available options.
 * Standard users get the closest standard resolution; advanced users keep their selection.
 *
 * @param cur - The currently selected resolution.
 * @param list - List of available resolutions.
 * @param userType - 'standard' or 'advanced' user type (default: 'standard').
 * @returns The closest resolution.
 */
export const getClosestResolutionForUser = (
  cur: Resolutions,
  list: Resolutions[],
  userType: UserType = 'standard'
) => {
  return userType === 'advanced' ? cur : findRes(cur, list, [Resolutions.RAW]);
};

/**
 * * Filters resolution options based on user entitlement and feature flag.
 */
const filterOptions = (
  ops: ResolutionOption[],
  userType: UserType = 'standard'
): ResolutionOption[] => {
  // * Ensure options exist
  if (!ops || ops.length === 0) {
    return [];
  }

  // ? Advanced users: return all options
  if (userType === 'advanced') {
    return ops;
  }

  const standardRes = [
    Resolutions['15-MINUTE'], // Closest to RAW
    Resolutions['30-MINUTE'],
    Resolutions.HOURLY,
    Resolutions.DAILY,
    Resolutions.WEEKLY,
    Resolutions.MONTHLY,
    'AUTO' as Resolutions, // Keep Auto for standard users
  ];

  // ? Standard users: filter to standard resolutions only
  return ops.filter((opt) => standardRes.includes(opt.value as Resolutions));
};

export const prepareResolutionOptions = (
  options: ResolutionOption[],
  userType: UserType = 'standard',
  current?: ResolutionOption['value']
): ResolutionOption[] => {
  // * Filter options based on user entitlement
  const filtered = filterOptions(options, userType);

  // * For standard users: if current selection is RAW, keep it in the list
  const isRawCurrent = current === Resolutions.RAW;
  const hasRawInFiltered = filtered.some(
    (opt) => opt.value === Resolutions.RAW
  );

  if (userType !== 'advanced' && isRawCurrent && !hasRawInFiltered) {
    // Add RAW to the list if it's currently selected but not in filtered options
    const rawOption = options.find((opt) => opt.value === Resolutions.RAW);
    if (rawOption) {
      filtered.push(rawOption);
    }
  }

  return filtered;
};
